import React, { useCallback, useState, useRef } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import DropdownMenu, {
	type CustomTriggerProps,
	type OnOpenChangeArgs,
	DropdownItem,
	DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import ExpandIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { SwimlaneModeId } from '@atlassian/jira-platform-board-kit/src/ui/swimlane/types.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { type SwimlaneMode, NO_SWIMLANE } from '../../../../model/swimlane/swimlane-modes.tsx';
import messages from './messages.tsx';

type Props = {
	swimlaneModes: SwimlaneMode[];
	setSwimlaneMode: (arg1: SwimlaneModeId, arg2?: UIAnalyticsEvent) => void;
	currentSwimlaneMode: SwimlaneModeId | null | undefined;
};

const SwimlaneSwitch = ({ swimlaneModes, setSwimlaneMode, currentSwimlaneMode }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownMenuRef = useRef<HTMLDivElement>(null);

	const onCloseFocusHandler = () => {
		requestAnimationFrame(() => dropdownMenuRef.current?.querySelector('button')?.focus());
	};

	const onDropdownOpenChange = ({ isOpen }: OnOpenChangeArgs) => {
		setIsDropdownOpen(isOpen);
		if (!isOpen) {
			onCloseFocusHandler();
		}
	};

	const renderTrigger = useCallback(
		({ triggerRef, ...triggerProps }: CustomTriggerProps) => {
			const swimlaneMode: SwimlaneMode =
				swimlaneModes.find(({ id }) => id === currentSwimlaneMode) || NO_SWIMLANE;
			let swimlaneModeText = formatMessage(swimlaneMode.message, swimlaneMode.values);

			if (isVisualRefreshEnabled()) {
				swimlaneModeText =
					swimlaneMode && swimlaneMode.id !== NO_SWIMLANE.id
						? formatMessage(swimlaneMode.message, swimlaneMode.values)
						: '';
			}

			const isSelected =
				triggerProps.isSelected || (isVisualRefreshEnabled() && Boolean(swimlaneModeText));

			// Hide the icon if there is swimlane mode text
			const iconAfter =
				isVisualRefreshEnabled() && swimlaneModeText ? undefined : (
					<ChevronDownIcon
						label=""
						LEGACY_fallbackIcon={ExpandIcon}
						LEGACY_size="medium"
						{
							// eslint-disable-next-line jira/ff/no-preconditioning
							...(isVisualRefreshEnabled() &&
								fg('visual-refresh-headers-buttons') &&
								!isSelected && {
									color: token('color.icon.subtle'),
								})
						}
						{...(!getIsSoftwareThemingEnabled() && {
							LEGACY_primaryColor: token('color.icon'),
							color: token('color.icon'),
						})}
					/>
				);

			const ButtonComponent = getIsSoftwareThemingEnabled() ? ThemedButton : Button;
			const triggerContent = (
				<>
					{!isVisualRefreshEnabled() && swimlaneModeText}
					{isVisualRefreshEnabled() && formatMessage(messages.groupCardsBy)}
					{swimlaneModeText && isVisualRefreshEnabled() && `: ${swimlaneModeText}`}
				</>
			);
			const trigger = (
				<ButtonComponent
					{...triggerProps}
					ref={triggerRef}
					id="swimlane-switch-menu-trigger"
					aria-label={
						isVisualRefreshEnabled()
							? formatMessage(messages.groupCardsByAccessability, {
									label: swimlaneModeText,
								})
							: undefined
					}
					iconAfter={iconAfter}
					isSelected={isSelected}
				>
					{isVisualRefreshEnabled() && fg('visual-refresh-headers-buttons') ? (
						<Box xcss={triggerStyles}>
							<Text
								{...(isSelected
									? { color: 'inherit' }
									: {
											color: 'color.text.subtle',
										})}
								weight="medium"
							>
								{triggerContent}
							</Text>
						</Box>
					) : (
						<TriggerContainer>
							<TriggerLabel>{triggerContent}</TriggerLabel>
						</TriggerContainer>
					)}
				</ButtonComponent>
			);

			return isDropdownOpen ? trigger : <Tooltip content={swimlaneModeText}>{trigger}</Tooltip>;
		},
		[currentSwimlaneMode, formatMessage, isDropdownOpen, swimlaneModes],
	);

	const onDropdownItemClick = useCallback(
		(swimlaneModeId: SwimlaneModeId) => () => {
			fireUIAnalytics(createAnalyticsEvent({ actionSubject: 'dropdownItem', action: 'clicked' }), {
				name: 'setSwimlaneMode',
				swimlaneMode: swimlaneModeId,
			});
			setSwimlaneMode(swimlaneModeId);
		},
		[setSwimlaneMode, createAnalyticsEvent],
	);

	return (
		<SwimlaneSwitchContainer
			data-testid="software-board.header.controls-bar.swimlane-switch"
			ref={dropdownMenuRef}
		>
			{!isVisualRefreshEnabled() && (
				<SwimlaneSwitchLabel htmlFor="swimlane-switch-menu-trigger">
					{formatMessage(messages.groupCardsByOld)}
				</SwimlaneSwitchLabel>
			)}
			<DropdownMenu
				isOpen={isDropdownOpen}
				onOpenChange={onDropdownOpenChange}
				trigger={renderTrigger}
				placement="bottom-end"
				{...(fg('tanuki_a11y_fixes_drop_2') ? { shouldRenderToParent: true } : {})}
			>
				<DropdownItemGroup>
					{swimlaneModes.map(({ id, message, values }) => (
						<DropdownItem
							key={id}
							data-testid="software-board.header.controls-bar.swimlane-switch.dropdown-item"
							onClick={onDropdownItemClick(id)}
						>
							{formatMessage(message, values)}
						</DropdownItem>
					))}
				</DropdownItemGroup>
			</DropdownMenu>
		</SwimlaneSwitchContainer>
	);
};

export default SwimlaneSwitch;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SwimlaneSwitchContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SwimlaneSwitchLabel = styled.label({
	font: token('font.body.small'),
	color: token('color.text.subtlest'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	textTransform: 'uppercase',
	paddingTop: 0,
	paddingRight: token('space.100'),
	paddingBottom: 0,
	paddingLeft: token('space.100'),
});

const triggerStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	maxWidth: `${gridSize * 15}px`,
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TriggerContainer = styled.span({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TriggerLabel = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 15}px`,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
